import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Car, Wallet, FileText, LogOut, Building2, ArrowLeftRight, Users as UsersIcon, BarChart3, Activity, Percent, BookOpen, ClipboardList } from "lucide-react";
import { Toaster } from "sonner";
import NotificationsBell from "@/components/NotificationsBell";

const SUB_DEALERS = ["Alok Automobile", "Dilip Enterprises"];

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "sidebar-dashboard-link", roles: ["admin", "branch", "finance", "pdi"] },
  { to: "/vehicles", label: "Vehicle Stock", icon: Car, testid: "sidebar-vehicles-link", roles: ["admin", "branch", "finance", "pdi"] },
  { to: "/transfers", label: "Transfer Register", icon: ArrowLeftRight, testid: "sidebar-transfers-link", roles: ["admin", "branch", "pdi"] },
  { to: "/finance", label: "Finance", icon: Wallet, testid: "sidebar-finance-link", roles: ["admin", "finance", "branch"] },
  { to: "/quotations", label: "Quotations", icon: ClipboardList, testid: "sidebar-quotations-link", roles: ["admin", "branch", "finance"] },
  { to: "/invoices", label: "Invoices", icon: FileText, testid: "sidebar-invoices-link", roles: ["admin", "finance", "branch"] },
  { to: "/incentives", label: "Incentive Master", icon: Percent, testid: "sidebar-incentives-link", roles: ["admin"] },
  { to: "/ledger", label: "Profit Ledger", icon: BookOpen, testid: "sidebar-ledger-link", roles: ["admin", "branch"], subDealerOnly: true },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "sidebar-reports-link", roles: ["admin", "finance"] },
  { to: "/audit", label: "Audit Log", icon: Activity, testid: "sidebar-audit-link", roles: ["admin"] },
  { to: "/users", label: "Users", icon: UsersIcon, testid: "sidebar-users-link", roles: ["admin"] },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="w-64 bg-slate-900 text-slate-300 flex flex-col fixed inset-y-0 left-0" data-testid="app-sidebar">
        <div className="px-6 py-6 border-b border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-11 h-11 rounded-md bg-white flex items-center justify-center overflow-hidden p-1">
                <img src="/atul-logo.png" alt="Atul Auto" className="w-full h-full object-contain" />
              </div>
              <div>
                <div className="font-heading font-bold text-white text-sm leading-tight">India Atul</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">Motors</div>
              </div>
            </div>
            <NotificationsBell />
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.filter(n => {
            if (!n.roles.includes(user?.role)) return false;
            // Ledger visible to admin OR branch user assigned to a sub-dealer branch
            if (n.subDealerOnly && user?.role === "branch" && !SUB_DEALERS.includes(user?.branch)) return false;
            return true;
          }).map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>
        <div className="px-4 py-4 border-t border-slate-800">
          <div className="text-xs text-slate-400 mb-2">Logged in as</div>
          <div className="text-sm text-white font-medium" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-xs text-slate-400 mb-3">
            <span className="uppercase font-semibold text-orange-400" data-testid="sidebar-user-role">{user?.role}</span>
            {user?.branch ? ` · ${user.branch}` : ""}
          </div>
          <button
            data-testid="sidebar-logout-button"
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-sm text-white transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </aside>
      <main className="flex-1 ml-64 p-8">
        {children}
      </main>
      <Toaster position="top-right" richColors />
    </div>
  );
}
